import React from "react";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Nav, Tab } from "react-bootstrap";
import { useNavigate, useSearchParams } from "react-router-dom";
import { dealActivities } from "data/crm/dealDetailsData";
import { attachments } from "data/project-management/todoListData";
import {
  faBrain,
  faChartLine,
  faClipboard,
  faEnvelope,
  faPaperclip,
  faPhone,
  faSquareCheck,
  faUsers,
  faVideo,
} from "@fortawesome/free-solid-svg-icons";
import DealDetailsActivity from "../deals-details/DealDetailsActivity";
import DealDetailsCall from "../deals-details/DealDetailsCall";
import DealDetailsNotes from "./DealDetailsNotes";
import DealDetailsTask from "./DealDetailsTask";
import DealDetailsAttachments from "./DealDetailsAttachments";
import DealDetailsMeeting from "./DealDetailsMeeting";
import DealDetailsTable from "components/tables/DealDetailsTable";
import DealRelatedPartiesCard from "components/cards/DealRelatedPartiesCard";
import DealAiAssistant from "../DealAiAssistant";
import DealEmails from "../DealEmails";
import CustomLoader from "components/common/CustomLoader";

interface TabItem {
  name: string;
  icon: IconProp;
  content: JSX.Element;
  key: string;
}

const DealDetailsTab = ({
  tasks,
  notes,
  activities,
  meetingNotes,
  callNotes,
  attachmentsLv,
  handleSearchInputChange,
  handleSearchMeeting,
  handleSearchTask,
  relatedParties,
  dealDetails,
  isLoading,
}: any) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const type = searchParams.get("type");
  
  // Get current active tab
  const activeTab = type?.toLowerCase() || "activity";

  // Lazy loading function - only render content when tab is active
  const renderTabContent = (tabKey: string, content: JSX.Element) => {
    if (activeTab === tabKey) {
      return content;
    }
    return <div>Loading...</div>; // Placeholder for inactive tabs
  };

  const tabitems: TabItem[] = [
    {
      name: "Activity",
      key: "activity",
      icon: faChartLine,
      content: renderTabContent("activity", (
        <DealDetailsActivity
          activities={dealActivities}
          allActivities={activities}
          handleSearchInputChange={handleSearchInputChange}
        />
      )),
    },
    {
      name: "Emails",
      key: "emails",
      icon: faEnvelope,
      content: renderTabContent("emails", <DealEmails dealDetails={dealDetails} />),
    },
    {
      name: "Call",
      key: "call",
      icon: faPhone,
      content: renderTabContent("call", <DealDetailsCall phoneNotes={callNotes} />),
    },
    {
      name: "Meeting",
      key: "meeting",
      icon: faVideo,
      content: renderTabContent("meeting", (
        <DealDetailsMeeting
          meetings={meetingNotes}
          handleSearchMeeting={handleSearchMeeting}
        />
      )),
    },
    {
      name: "Notes",
      key: "notes",
      icon: faClipboard,
      content: renderTabContent("notes", <DealDetailsNotes notes={notes} />),
    },
    {
      name: "Task",
      key: "task",
      icon: faSquareCheck,
      content: renderTabContent("task", (
        <DealDetailsTask tasks={tasks} handleSearchTask={handleSearchTask} />
      )),
    },
    {
      name: "Attachments",
      key: "attachments",
      icon: faPaperclip,
      content: renderTabContent("attachments", (
        <DealDetailsAttachments
          attachments={attachments}
          attachmentsLv={attachmentsLv}
        />
      )),
    },
    {
      name: "AI Assistant",
      key: "ai assistant",
      icon: faBrain,
      content: renderTabContent("ai assistant", <DealAiAssistant dealDetails={dealDetails} />),
    },
    {
      name: "Related Parties",
      key: "related parties",
      icon: faUsers,
      content: renderTabContent("related parties", (
        <div>
          <DealRelatedPartiesCard 
            relatedParties={relatedParties}
            dealId={dealDetails?.id}
            className="mb-4"
          />
          <div className="advanced-table-section">
            <h6 className="mb-3 text-body-emphasis">Detailed View</h6>
            <DealDetailsTable relatedParties={relatedParties} />
          </div>
        </div>
      )),
    },
  ];

  const handleAddQueryParams = (value: any) => {
    const queryParams = new URLSearchParams({
      type: value,
    });

    navigate(`?${queryParams.toString().toLowerCase()}`);
  };

  return (
    <div className="modern-deal-tabs">
      <Tab.Container activeKey={activeTab}>
        {/* Modern Tab Navigation */}
        <div className="modern-tab-wrapper">
          <Nav className="modern-nav-tabs" role="tablist">
            {tabitems.map((item) => (
              <Nav.Item key={item.name} className="modern-nav-item">
                <Nav.Link 
                  eventKey={item.name.toLowerCase()}
                  onClick={() => handleAddQueryParams(item.name)}
                  className="modern-nav-link d-flex align-items-center gap-2"
                >
                  <div className="tab-icon-wrapper">
                    <FontAwesomeIcon
                      icon={item.icon}
                      className="tab-icon"
                    />
                  </div>
                  <span className="tab-label">{item.name}</span>
                </Nav.Link>
              </Nav.Item>
            ))}
          </Nav>
        </div>

        {/* Tab Content */}
        <div className="modern-tab-content">
          {isLoading ? (
            <div className="d-flex justify-content-center align-items-center py-5">
              <CustomLoader />
            </div>
          ) : (
            <Tab.Content>
              {tabitems.map((item) => (
                <Tab.Pane key={item.name} eventKey={item.name.toLowerCase()}>
                  <div className="tab-pane-content">
                    {item.content}
                  </div>
                </Tab.Pane>
              ))}
            </Tab.Content>
          )}
        </div>
      </Tab.Container>

      {/* Modern Tab Styles */}
      <style dangerouslySetInnerHTML={{
        __html: `
        .modern-deal-tabs {
          --tab-primary-color: #0d6efd;
          --tab-border-color: #e9ecef;
          --tab-bg-color: #ffffff;
          --tab-hover-bg: #f8f9fa;
          --tab-active-bg: #ffffff;
          --tab-text-color: #6c757d;
          --tab-active-text: #212529;
          --tab-border-radius: 12px;
          --tab-shadow: 0 2px 4px rgba(0,0,0,0.05);
        }

        .modern-tab-wrapper {
          background: var(--tab-bg-color);
          border: 1px solid var(--tab-border-color);
          border-radius: var(--tab-border-radius);
          padding: 4px;
          margin-bottom: 2rem;
          box-shadow: var(--tab-shadow);
          overflow-x: auto;
          scrollbar-width: none;
          -ms-overflow-style: none;
        }

        .modern-tab-wrapper::-webkit-scrollbar {
          display: none;
        }

        .modern-nav-tabs {
          border: none;
          gap: 2px;
          flex-wrap: nowrap;
          min-width: max-content;
        }

        .modern-nav-item {
          flex-shrink: 0;
        }

        .modern-nav-link {
          border: none !important;
          background: transparent !important;
          color: var(--tab-text-color) !important;
          padding: 12px 16px !important;
          border-radius: 8px !important;
          transition: all 0.2s ease !important;
          font-weight: 500 !important;
          font-size: 0.9rem !important;
          white-space: nowrap !important;
          position: relative !important;
          min-height: 44px !important;
        }

        .modern-nav-link:hover {
          background: var(--tab-hover-bg) !important;
          color: var(--tab-active-text) !important;
          transform: translateY(-1px);
        }

        .modern-nav-link.active {
          background: var(--tab-primary-color) !important;
          color: white !important;
          box-shadow: 0 2px 8px rgba(13, 110, 253, 0.25) !important;
        }

        .modern-nav-link.active .tab-icon-wrapper {
          background: rgba(255, 255, 255, 0.2);
        }

        .tab-icon-wrapper {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 20px;
          height: 20px;
          border-radius: 4px;
          transition: all 0.2s ease;
        }

        .tab-icon {
          font-size: 0.85rem !important;
          transition: all 0.2s ease;
        }

        .tab-label {
          font-size: 0.9rem;
          font-weight: 500;
          transition: all 0.2s ease;
        }

        .modern-tab-content {
          position: relative;
          background: var(--tab-bg-color, #ffffff);
          padding: 1.5rem;
          border-radius: var(--tab-border-radius, 12px);
          border: 1px solid var(--tab-border-color, #e9ecef);
          box-shadow: var(--tab-shadow, 0 2px 4px rgba(0,0,0,0.05));
        }

        .tab-pane-content {
          animation: fadeIn 0.3s ease-in-out;
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @media (max-width: 768px) {
          .modern-tab-wrapper {
            margin: 0 -1rem 2rem -1rem;
            border-radius: 0;
            border-left: none;
            border-right: none;
          }

          .modern-nav-link {
            padding: 10px 12px !important;
            font-size: 0.85rem !important;
          }

          .tab-label {
            font-size: 0.85rem;
          }
        }
        
        @media (max-width: 500px) {
          .modern-tab-content {
            padding: 1rem;
            height: auto;
            min-height: 200px;
            max-height: none;
            overflow: hidden;
            scrollbar-width: none;  /* Firefox */
            -ms-overflow-style: none;  /* IE and Edge */
          }
          
          .modern-tab-content::-webkit-scrollbar {
            display: none;  /* Chrome, Safari, Opera */
          }
          
          .tab-pane-content {
            height: auto;
          }
        }

        .modern-nav-link:focus {
          outline: 2px solid var(--tab-primary-color);
          outline-offset: 2px;
        }
        `
      }} />
    </div>
  );
};

export default DealDetailsTab;
